import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage } from '../../types';
import { api } from '../../services/apiService';

interface Props {
  message: ChatMessage;
  isMine: boolean;
  isAdmin?: boolean;           // l'observateur courant est-il COMPANY_ADMIN ?
  showSender: boolean;         // afficher le nom (groupes / changement d'expéditeur)
  onEdit?: (messageId: string, currentContent: string) => void;
  onDelete?: (messageId: string) => void;
  onReact?: (messageId: string, emoji: string) => void;
}

const ALLOWED_REACTIONS = ['👍', '❤️', '😄', '😮', '😢', '🎉', '✅', '👀'] as const;

function formatTime(iso: string): string {
  const d = new Date(iso.replace(' ', 'T'));
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

// Linkifier basique : convertit les http(s)://... en liens cliquables et
// met en surbrillance les mentions @nom. Tout le reste est rendu en texte
// brut (jamais d'HTML interprété).
function renderContent(content: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  // URL OU mention — capture la première occurrence
  const regex = /(https?:\/\/[^\s]+)|(@[A-Za-zÀ-ÿ0-9_\-]{2,40})/gu;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) parts.push(content.substring(lastIndex, match.index));
    if (match[1]) {
      parts.push(
        <a key={`link-${key++}`} href={match[1]} target="_blank" rel="noopener noreferrer"
           className="text-cyan-300 underline hover:text-cyan-200">{match[1]}</a>
      );
    } else if (match[2]) {
      parts.push(
        <span key={`mention-${key++}`}
              className="font-bold text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/15 rounded px-1">
          {match[2]}
        </span>
      );
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < content.length) parts.push(content.substring(lastIndex));
  return parts;
}

// Le message est-il encore éditable (auteur + < 15 min depuis l'envoi) ?
function isEditable(createdAt: string): boolean {
  const ts = new Date(createdAt.replace(' ', 'T')).getTime();
  if (!ts) return false;
  return (Date.now() - ts) < 15 * 60 * 1000;
}

const MessageBubble: React.FC<Props> = ({ message, isMine, isAdmin, showSender, onEdit, onDelete, onReact }) => {
  const isDeleted = !!message.deleted_at;
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  // Fermer les popovers si clic à l'extérieur
  useEffect(() => {
    if (!menuOpen && !pickerOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen, pickerOpen]);

  const canEdit = isMine && !isDeleted && isEditable(message.created_at) && !!onEdit;
  const canDelete = !isDeleted && !!onDelete && (isMine || isAdmin);
  const canReact = !isDeleted && !!onReact;
  const hasMenu = canEdit || canDelete;
  const reactions = message.reactions || [];

  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-2 group`}>
      <div className={`max-w-[70%] ${isMine ? 'items-end' : 'items-start'} flex flex-col`}>
        {showSender && !isMine && (
          <span className="text-[10px] font-bold text-slate-500 dark:text-[#7f93bc] px-3 mb-0.5">{message.sender_name}</span>
        )}

        <div className="relative flex items-center gap-1">
          {/* Bouton menu (apparait au hover sur ses propres messages) */}
          {hasMenu && isMine && (
            <div ref={menuRef} className="relative opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => setMenuOpen(o => !o)}
                className="p-1 rounded-md hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-[#7f93bc] hover:text-slate-900 dark:hover:text-[#e0eaff]"
                aria-label="Actions sur le message"
                title="Actions"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
              {menuOpen && (
                <div className="absolute bottom-full right-0 mb-1 min-w-[140px] rounded-lg border border-slate-200 dark:border-[rgba(100,140,210,0.3)] bg-white dark:bg-[rgba(15,30,60,0.97)] backdrop-blur shadow-2xl z-20 py-1">
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); onEdit!(message.id, message.content); }}
                      className="block w-full text-left px-3 py-1.5 text-xs text-slate-700 dark:text-[#e0eaff] hover:bg-cyan-50 dark:hover:bg-cyan-500/15"
                    >
                      ✏️ Modifier
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); onDelete!(message.id); }}
                      className="block w-full text-left px-3 py-1.5 text-xs text-rose-600 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/15"
                    >
                      🗑 Supprimer
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div
            className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
              isMine
                ? 'bg-cyan-100 dark:bg-cyan-600/30 border border-cyan-300 dark:border-cyan-500/40 text-slate-900 dark:text-[#e0eaff] rounded-br-md'
                : 'bg-white dark:bg-[rgba(100,140,210,0.12)] border border-slate-200 dark:border-[rgba(100,140,210,0.25)] text-slate-900 dark:text-[#e0eaff] rounded-bl-md'
            } ${isDeleted ? 'italic opacity-60' : ''}`}
          >
            {isDeleted ? <em>Message supprimé</em> : renderContent(message.content)}
            {/* Pièces jointes (Phase 2) */}
            {!isDeleted && message.attachments && message.attachments.length > 0 && (
              <div className="mt-2 flex flex-col gap-1">
                {message.attachments.map(a => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => { api.chatDownloadAttachment(a.id).catch(() => { /* silencieux */ }); }}
                    className="text-left text-xs flex items-center gap-2 px-2 py-1 rounded-md bg-white/40 dark:bg-black/20 hover:bg-white/70 dark:hover:bg-black/40 border border-slate-200 dark:border-[rgba(100,140,210,0.25)]"
                    title={`Télécharger ${a.original_name} (${Math.round(a.size_bytes / 1024)} Ko)`}
                  >
                    <span>📎</span>
                    <span className="truncate max-w-[200px]">{a.original_name}</span>
                    <span className="opacity-60">({Math.round(a.size_bytes / 1024)} Ko)</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Bouton réaction (smiley) — apparaît au hover */}
          {canReact && (
            <div ref={pickerRef} className="relative opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => setPickerOpen(o => !o)}
                className="p-1 rounded-md hover:bg-slate-200 dark:hover:bg-white/10 text-slate-500 dark:text-[#7f93bc] hover:text-slate-900 dark:hover:text-[#e0eaff]"
                aria-label="Ajouter une réaction"
                title="Ajouter une réaction"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                        d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              {pickerOpen && (
                <div className={`absolute bottom-full mb-1 ${isMine ? 'right-0' : 'left-0'} rounded-full border border-slate-200 dark:border-[rgba(100,140,210,0.3)] bg-white dark:bg-[rgba(15,30,60,0.97)] backdrop-blur shadow-2xl z-20 px-2 py-1 flex gap-1`}>
                  {ALLOWED_REACTIONS.map(em => (
                    <button
                      key={em}
                      type="button"
                      onClick={() => { setPickerOpen(false); onReact!(message.id, em); }}
                      className="text-lg hover:scale-125 transition-transform px-1"
                      aria-label={`Réagir avec ${em}`}
                      title={em}
                    >
                      {em}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Bouton supprimer côté admin pour les messages reçus */}
          {hasMenu && !isMine && canDelete && (
            <button
              type="button"
              onClick={() => onDelete!(message.id)}
              className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-rose-50 dark:hover:bg-rose-500/15 text-slate-500 dark:text-[#7f93bc] hover:text-rose-600 dark:hover:text-rose-300 transition-opacity"
              aria-label="Supprimer (admin)"
              title="Supprimer (action admin)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
              </svg>
            </button>
          )}
        </div>

        {/* Réactions agrégées sous la bulle */}
        {!isDeleted && reactions.length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${isMine ? 'justify-end' : 'justify-start'}`}>
            {reactions.map(r => (
              <button
                key={r.emoji}
                type="button"
                onClick={() => onReact && onReact(message.id, r.emoji)}
                disabled={!canReact}
                title={r.users.join(', ')}
                className={`text-[11px] leading-none px-2 py-1 rounded-full border transition-colors ${
                  r.me
                    ? 'bg-cyan-100 dark:bg-cyan-600/40 border-cyan-400 dark:border-cyan-400/60 text-slate-900 dark:text-[#e0eaff]'
                    : 'bg-white dark:bg-[rgba(100,140,210,0.08)] border-slate-200 dark:border-[rgba(100,140,210,0.25)] text-slate-700 dark:text-[#a5b8d8] hover:bg-slate-50 dark:hover:bg-[rgba(100,140,210,0.16)]'
                } ${canReact ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <span className="mr-1">{r.emoji}</span>
                <span className="font-bold">{r.count}</span>
              </button>
            ))}
          </div>
        )}

        <span className="text-[9px] text-slate-400 dark:text-[#546a96] px-3 mt-0.5">
          {formatTime(message.created_at)}
          {message.edited_at && !isDeleted && <span className="ml-1">(modifié)</span>}
        </span>
      </div>
    </div>
  );
};

export default MessageBubble;
